import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider } from 'react-hook-form';
import { ControlledPassword } from '../../../../forms/controlled-password/controlled-password';
import { useForm } from '../../../../hooks/use-form';
import { ControlledTextField } from '../../../../inputs/forms/controlled-text-field/controlled-text-field';
import { Button } from '../../../../primitives/button';
import { Typography } from '../../../../primitives/typography/typography';
import { describeInviteFailure } from '../invite-error-content';
import { inviteAcceptSchema, type InviteAcceptFormData } from '../invited-page.types';

export type InviteAcceptFormProps = {
  /**
   * Runs on a valid submit. Reject to surface a form-level error — the rejection's `message` is
   * shown inline above the submit button, so callers do not need their own catch.
   */
  onSubmit: (formData: InviteAcceptFormData) => Promise<unknown>;
  submitLabel?: string;
};

/**
 * Account-creation form for accepting a venue invite. Owns its own schema and form state; the
 * surrounding page chrome (logo, headings, OAuth) stays with the consuming app.
 */
export const InviteAcceptForm = ({ onSubmit, submitLabel = 'Sign Up' }: InviteAcceptFormProps) => {
  const methods = useForm<InviteAcceptFormData>({
    resolver: zodResolver(inviteAcceptSchema),
    defaultValues: {},
    mode: 'onChange',
  });

  const handleSubmit = async (formData: InviteAcceptFormData) => {
    try {
      await onSubmit(formData);
    } catch (err) {
      methods.setError('root', {
        type: 'manual',
        message: describeInviteFailure(err, 'Failed to sign up. Please try again.'),
      });
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(handleSubmit)} autoComplete="off">
        <ControlledTextField
          name="name"
          label="Fullname"
          control={methods.control}
          autoFocus
          required
        />
        <ControlledTextField
          name="phone"
          label="Phone number"
          control={methods.control}
          type="tel"
        />
        <ControlledPassword name="password" label="Password" control={methods.control} required />
        <ControlledPassword
          name="confirmPassword"
          label="Confirm password"
          control={methods.control}
        />
        {methods.formState.errors.root && (
          <Typography.Caption sx={{ mb: 2, color: 'error.main', textAlign: 'center' }}>
            {methods.formState.errors.root.message}
          </Typography.Caption>
        )}
        <Button
          type="submit"
          fullWidth
          size="large"
          disabled={methods.formState.isSubmitting}
          loading={methods.formState.isSubmitting}
          sx={{ mt: 3, mb: 2, textTransform: 'none' }}
        >
          {submitLabel}
        </Button>
      </form>
    </FormProvider>
  );
};
